import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

// Import SVG icons
import CalendarIcon from "../assets/icons/calendar.svg";
import SettingsSlidersIcon from "../assets/icons/settings-sliders.svg";
import CommentAltIcon from "../assets/icons/comment-alt.svg";
import FileMedicalAltIcon from "../assets/icons/file-medical-alt.svg";
import TrophyStarIcon from "../assets/icons/trophy-star.svg";
import ListCheckIcon from "../assets/icons/list-check.svg";

import styles from './styles/GroupDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member {
  user_id: number;
  username: string;
  display_name: string;
  active_level?: {
    name: string | null;
    completed_skills_count: number | null;
    skills_count: number | null;
    attendances: number | null;
  };
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Group {
  group_id: number;
  name: string;
  members: Member[];
  curriculum_id?: number;
}

interface RouteParams {
  [key: string]: string | undefined;
  group_id?: string;
}

const GroupDetailsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { group_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState<string>("");
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<number | null>(null);
  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string>("");
  const hasTrackedRef = useRef(false);

  // Track page visit once we have the group name
  useEffect(() => {
    if (groupName && !hasTrackedRef.current) {
      trackPageVisit(`Group: ${groupName}`, `/groups/${group_id}`);
      hasTrackedRef.current = true;
    }
  }, [groupName, group_id]);

  useEffect(() => {
    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data = await response.json();
        setCurriculums(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchCurriculums();
  }, [token, selectedOrgID]);

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups/${group_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch group.");
          return;
        }

        const data: Group = await response.json();
        if (data) {
          setGroupName(data.name || "");
          setGroupMembers(data.members || []);
          setSelectedCurriculum(data.curriculum_id || null);
        } else {
          setError("No data received for the group.");
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchGroupDetails();
  }, [group_id, token, selectedOrgID]);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Group Details</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      
      <div className={styles.groupInfo}>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Group Name:</span>
          <span className={styles.infoValue}>{groupName}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Curriculum:</span>
          <span className={styles.infoValue}>
            {selectedCurriculum 
              ? curriculums.find(c => c.curriculum_id === selectedCurriculum)?.name || 'Unknown'
              : 'None'}
          </span>
        </div>
      </div>

      <div className={styles.actionsSection}>
        <h4 className={styles.actionsTitle}>Actions</h4>
        <div className={styles.actionsColumns}>
          <div className={styles.actionsColumn}>
            <ul className={styles.actionsList}>
              <li className={styles.actionItem}>
                <a 
                  className={styles.actionLink} 
                  href={`/groups/${group_id}/attendance`}
                >
                  <img src={CalendarIcon} alt="" className={styles.actionIcon} />
                  Attendance
                </a>
              </li>
              <li className={styles.actionItem}>
                <a 
                  className={styles.actionLink} 
                  href={`/groups/${group_id}/snippets`}
                >
                  <img src={FileMedicalAltIcon} alt="" className={styles.actionIcon} />
                  Snippets
                </a>
              </li>
              <li className={styles.actionItem}>
                <a 
                  className={styles.actionLink} 
                  href={`/groups/${group_id}/edit`}
                >
                  <img src={SettingsSlidersIcon} alt="" className={styles.actionIcon} />
                  Edit Group
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.membersSection}>
        <h3 className={styles.membersTitle}>Group Members</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Member</th>
              <th className={styles.tableHeader}>Level</th>
              <th className={styles.tableHeader}>Skills</th>
              <th className={styles.tableHeader}>Attds</th>
              <th className={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupMembers.length > 0 ? (
              groupMembers.map((member) => (
                <tr key={member.user_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>
                    <a href={`/members/${member.user_id}`} className={styles.memberLink}>
                      {member.display_name 
                        ? `${member.display_name} (${member.username})`
                        : member.username}
                    </a>
                  </td>
                  <td className={styles.tableCell}>
                    <span className={!member.active_level ? styles.notEnrolled : undefined}>
                      {member.active_level?.name || 
                        `Not enrolled in ${curriculums.find(c => c.curriculum_id === selectedCurriculum)?.name || 'curriculum'}`}
                    </span>
                  </td>
                  <td className={`${styles.tableCell} ${styles.skillsCell}`}>
                    {member.active_level ? (
                      <>
                        <div className={styles.progressContainer}>
                          <div 
                            className={styles.progressBar} 
                            style={{ 
                              width: `${((member.active_level.completed_skills_count || 0) / 
                                (member.active_level.skills_count || 1)) * 100}%` 
                            }} 
                          />
                        </div>
                        <span className={styles.progressText}>
                          {`${member.active_level.completed_skills_count || 0}/${member.active_level.skills_count || 0}`}
                        </span>
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={styles.tableCell}>
                    {member.active_level?.attendances ?? '-'}
                  </td>
                  <td className={styles.tableCell}>
                    <div className={styles.memberActions}>
                      <a 
                        href="#"
                        className={styles.actionLink}
                        title="Notes"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/members/${member.user_id}/notes`, {
                            state: { selectedCurriculumId: selectedCurriculum }
                          });
                        }}
                      >
                        <img src={CommentAltIcon} alt="Notes" className={styles.actionIcon} />
                      </a>
                      
                      {selectedCurriculum && (
                        <>
                          <a 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/members/${member.user_id}/snippets`, {
                                state: { selectedCurriculum }
                              });
                            }}
                            className={styles.actionLink}
                            title="Snippets"
                          >
                            <img src={FileMedicalAltIcon} alt="Snippets" className={styles.actionIcon} />
                          </a>
                          
                          <a 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/members/${member.user_id}/attendance`, {
                                state: { selectedCurriculum }
                              });
                            }}
                            className={styles.actionLink}
                            title="Attendance"
                          >
                            <img src={CalendarIcon} alt="Attendance" className={styles.actionIcon} />
                          </a>
                          
                          <a 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/members/${member.user_id}/skills`, {
                                state: { selectedCurriculum }
                              });
                            }}
                            className={styles.actionLink}
                            title="Levels/Skills"
                          >
                            <img src={ListCheckIcon} alt="Skills" className={styles.actionIcon} />
                          </a>

                          <a 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/members/${member.user_id}/accomplishment`, {
                                state: { selectedCurriculum }
                              });
                            }}
                            className={styles.actionLink}
                            title="Accomplishments"
                          >
                            <img src={TrophyStarIcon} alt="Accomplishments" className={styles.actionIcon} />
                          </a>
                          
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className={styles.tableCell} colSpan={2}>No members found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupDetailsPage;
